import { loadImage } from './loaders';

export enum Dimension {
    WIDTH = 'width',
    HEIGHT = 'height',
}

type Dimensions = Record<Dimension, number>;

export type CanvasSizes = {
    [size: string]: CanvasSize,
};

type CanvasSize = Dimensions & {
    types: {
        [type: string]: ImageSize,
    },
};

type ImageSize = {
    size: number,
    dimension: Dimension,
    position: Position,
};

type CanvasConfig = Dimensions & {
    type: ImageSize,
};

export type Position = [number, number];

/**
 * Replaces multiple images.
 */
export default function replaceImages(images: HTMLImageElement[], config: CanvasSizes) {
    if (!isSupported()) {
        return Promise.resolve();
    }

    function getConfig(img: HTMLImageElement): CanvasConfig {
        const size = img.getAttribute('data-size');
        const type = img.getAttribute('data-type');

        if (size === null) {
            throw new Error('Image is missing "data-size" attribute.');
        }

        if (type === null) {
            throw new Error('Image is missing "data-type" attribute.');
        }

        if (config[size] === undefined) {
            throw new Error(`Config is missing a "${size}" entry.`);
        }

        const { width, height, types } = config[size];

        if (types[type] === undefined) {
            throw new Error(`Config is missing a "${type}" entry.`);
        }

        return { width, height, type: types[type] };
    }

    return Promise.all(images.map(image => {
        return replaceImage(image, getConfig(image));
    }));
}

/**
 * Replaces a single image.
 */
export function replaceImage(image: HTMLImageElement, { type, width, height }: CanvasConfig) {
    if (!isSupported()) {
        return Promise.resolve();
    }

    image.style.opacity = '0';

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    return drawImage(canvas, image, type).then(createCanvasUrl).then(url => {
        image.src = url;
    }).catch(error => {
        console.error(error);
    }).finally(() => {
        image.style.opacity = '';
    });
}

/**
 * Returns whether the current environment supports the <canvas> element.
 */
function isSupported() {
    const canvas = document.createElement('canvas');

    return !!(canvas.getContext && canvas.getContext('2d'));
}

/**
 * Creates a canvas object URL, either a (faster) blob object URL or a (slower)
 * data URL.
 */
function createCanvasUrl(canvas: HTMLCanvasElement): Promise<string> {
    if (canvas.toBlob) {
        return new Promise((resolve, reject) => {
            canvas.toBlob(blob => {
                if (blob === null) {
                    reject();
                } else {
                    resolve(URL.createObjectURL(blob));
                }
            });
        });
    }

    return Promise.resolve(canvas.toDataURL());
}

/**
 * Scales a set of dimensions proportionally.
 */
function scale(dimensions: Dimensions, { size, dimension }: ImageSize) {
    return [dimensions.width, dimensions.height].map(i => {
        return i * (size / dimensions[dimension]);
    });
}

/**
 * Returns the correct position for the image on the canvas.
 */
function positionImage(canvas: HTMLCanvasElement, dimensions: Dimensions, [x, y]: Position) {
    const xMultiplier = x / 100;
    const yMultiplier = y / 100;

    return [
        (canvas.width * xMultiplier) - (dimensions.width * xMultiplier),
        (canvas.height * yMultiplier) - (dimensions.height * yMultiplier),
    ];
}

/**
 * Draws an image onto the canvas.
 */
function drawImage(canvas: HTMLCanvasElement, img: HTMLImageElement, config: ImageSize) {
    return loadImage(img.src).then(() => {
        const [width, height] = scale({ width: img.naturalWidth, height: img.naturalHeight }, config).map(Math.floor);
        const [x, y] = positionImage(canvas, { width, height }, config.position).map(Math.floor);

        const context = canvas.getContext('2d')!;
        context.drawImage(img, x, y, width, height);

        return canvas;
    });
}
